import { useEffect } from 'react'
import { useAuth } from '../auth/useAuth'

/**
 * The Microsoft four-square, as four spans. `Frontend/signin.html` draws it in CSS — no image, no
 * CDN, and no vendor asset to keep in step with a brand refresh.
 */
/**
 * The build's own date, injected at build time. NOT `new Date()`: a footer that shows today is
 * telling the reader when they are looking, which they know, instead of what they are looking at.
 */
const BUILT = String(import.meta.env['VITE_BUILD_DATE'] ?? 'dev')

function MicrosoftMark(): React.JSX.Element {
  return <span className="ms" aria-hidden="true"><i /><i /><i /><i /></span>
}

/**
 * The only unauthenticated surface, and its three states. `04 §4.3`,
 * `Frontend/01-wireframe-signin.md`.
 *
 * <p><b>THE REFUSAL IS A DESIGNED PAGE, NOT AN ERROR.</b> `AC-AUTH-2` requires it and forbids a 404
 * or a generic failure — a person who is signed in and not entitled has done nothing wrong, and a
 * 404 tells them the system does not exist rather than that they need access to it.</p>
 */
export function SignIn(): React.JSX.Element {
  const { state, signIn, signOut } = useAuth()

  useEffect(() => {
    document.body.dataset['view'] = 'signin'
    return () => { delete document.body.dataset['view'] }
  }, [])

  if (state === 'not-entitled') {
    return (
      <>
        <div className="hero" aria-hidden="true" />
        <main className="sheet" id="main">
          <div className="col">
            <section className="denied">
              <div className="plate">
                <p className="mlabel" style={{ marginBottom: '10px' }}>Access</p>
                <h1>Access has not been granted</h1>
                <p className="what">
                  Your sign-in worked. Your account is not yet a member of the group that grants
                  access to the Economic Impact Advisor. Nothing is wrong with the application or
                  with your credentials.
                </p>

                {/* `§2.3` MUSTs this page name who to ask — "an authorisation failure that looks
                    like a bug generates a support ticket instead of an access request" — but
                    GENERICALLY. This page is reachable by someone who is NOT authorised, and the
                    account, the group name and any team address would tell a stranger who to
                    social-engineer while telling the person nothing they can act on. */}
                <p className="what" style={{ marginBottom: '26px' }}>
                  Ask your IT service desk for access.
                </p>

                <div className="action">
                  {/* Sign out is genuine here: they hold a valid Entra session, so clearing local
                      token state lets them retry as someone else. */}
                  <button type="button" className="btn quiet" onClick={() => void signOut()}>
                    Sign out
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>

      {/* The product name is the page's h1 and its <title>; repeating it in the footer of a page
          whose only other text is a button says nothing. VERSION AND DATE DO — and it anchors the
          composition, which is why the plate sits where it sits. */}
      <footer className="pagefoot">
        <span>v1 <span className="date">({BUILT})</span></span>
      </footer>
      </>
    )
  }

  return (
    <>
      {/* No lockup and no organisation name anywhere on this page. It is the one surface an
          unauthenticated visitor can reach, and which department operates the system is not
          something it should disclose. The palette, the gradient and the type stay: they are the
          product's skin, not a name. */}
      <div className="hero" aria-hidden="true" />
      <main className="sheet" id="main">
        <div className="col">
          <section>
            <div className="plate">
              <h1>Economic Impact Advisor</h1>
              <div className="action">
                <button type="button" className="btn primary signin" onClick={() => void signIn()}>
                  <MicrosoftMark />
                  Sign in with Microsoft
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* The product name is the page's h1 and its <title>; repeating it in the footer of a page
          whose only other text is a button says nothing. VERSION AND DATE DO — and it anchors the
          composition, which is why the plate sits where it sits. */}
      <footer className="pagefoot">
        <span>v1 <span className="date">({BUILT})</span></span>
      </footer>
    </>
  )
}

/**
 * Signed out mid-session. DURABILITY REASSURANCE, NO CAUSE STATED, NO ENGAGEMENT NAMED.
 *
 * <p>The cause is not stated because the honest ones are all alarming and none are actionable; the
 * engagement is not named because this banner may be seen by whoever is at the machine.</p>
 */
export function SignedOutBanner({ onSignIn }: { onSignIn: () => void }): React.JSX.Element {
  return (
    <>
      <div className="hero" aria-hidden="true" />
      <main className="sheet" id="main">
        <div className="col">
          <section className="lost">
            <div className="plate">
              <p className="mlabel" style={{ marginBottom: '10px' }}>Session</p>
              <h1>You have been signed out</h1>

              {/* NO CAUSE IS STATED. The honest ones are all alarming and none are actionable, and
                  `§2.3` calls engagement durability "the single most common thing a user will not
                  believe until told". */}
              <p className="kept">
                Nothing has been lost. Engagements run on the server, not in this browser, so
                signing out does not stop one. Anything waiting for you will still be waiting when
                you sign back in.
              </p>

              <div className="action">
                <button type="button" className="btn primary" onClick={onSignIn}>
                  <span className="ms" aria-hidden="true"><i /><i /><i /><i /></span>
                  Sign in again
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* The product name is the page's h1 and its <title>; repeating it in the footer of a page
          whose only other text is a button says nothing. VERSION AND DATE DO — and it anchors the
          composition, which is why the plate sits where it sits. */}
      <footer className="pagefoot">
        <span>v1 <span className="date">({BUILT})</span></span>
      </footer>
    </>
  )
}
